// Centralized API utility for making authenticated requests
// Automatically includes X-Club-Code header for superadmin impersonation

import { fetchThroughSleep } from './wakeBackend';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * Read the role claim from a JWT payload. Used only as a client-side guard
 * so non-superadmins never send X-Club-Code; the server still enforces this.
 */
export const getTokenRole = (token) => {
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const padded = part.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((part.length + 3) % 4);
    const payload = JSON.parse(atob(padded));
    return payload.role || null;
  } catch {
    return null;
  }
};

/**
 * Get authentication headers including impersonation header if applicable
 * @param {string} token - JWT token
 * @param {boolean} includeContentType - Whether to include Content-Type header (default: true)
 * @returns {Object} Headers object
 */
export const getAuthHeaders = (token, includeContentType = true) => {
  const headers = {
    'Authorization': `Bearer ${token}`
  };
  
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  
  // Impersonation is superadmin-only. Never attach X-Club-Code for other roles,
  // even if a leftover localStorage value is present on a shared browser.
  const impersonatedClubCode = localStorage.getItem('impersonatedClubCode');
  if (impersonatedClubCode && getTokenRole(token) === 'superadmin') {
    headers['X-Club-Code'] = impersonatedClubCode;
  }
  
  return headers;
};

/**
 * Make an authenticated API request
 * @param {string} endpoint - API endpoint (e.g., '/api/athletes')
 * @param {Object} options - Fetch options
 * @param {string} token - JWT token
 * @returns {Promise<Response>}
 */
export const apiFetch = async (endpoint, options = {}, token) => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
  
  const headers = getAuthHeaders(token, options.method !== 'GET');
  
  // Merge with any additional headers
  if (options.headers) {
    Object.assign(headers, options.headers);
  }
  
  return fetchThroughSleep(url, {
    ...options,
    headers
  });
};

/**
 * GET request helper
 */
export const apiGet = (endpoint, token) => {
  return apiFetch(endpoint, { method: 'GET' }, token);
};

/**
 * POST request helper
 */
export const apiPost = (endpoint, body, token) => {
  return apiFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(body)
  }, token);
};

/**
 * PUT request helper
 */
export const apiPut = (endpoint, body, token) => {
  return apiFetch(endpoint, {
    method: 'PUT',
    body: JSON.stringify(body)
  }, token);
};

/**
 * DELETE request helper
 */
export const apiDelete = (endpoint, token) => {
  return apiFetch(endpoint, { method: 'DELETE' }, token);
};

export default {
  getAuthHeaders,
  apiFetch,
  apiGet,
  apiPost,
  apiPut,
  apiDelete,
  API_URL
};

